import { useMemo, useState } from "preact/hooks"
import { ArrowLeft, ChevronDown, ChevronRight } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { exactCurrencyFormatter } from "../../../utils"
import type { TimePeriod } from "../types"
import { format_period_header } from "../utils/periods"
import { format_cases } from "../utils/quantity_basis"
import {
  build_version_compare_table,
  compare_delta_pct,
  type CompareCellPair,
  type CompareLeaf,
  type CompareMeasure,
  type VersionCompareRow,
} from "../utils/version_compare_table"

interface VersionCompareViewProps {
  /** This version's leaves (`flatten_compare_leaves` of what the grid shows). */
  this_version: CompareLeaf[]
  /** The compared version's leaves, already shifted onto this version's columns. */
  compared_version: CompareLeaf[]
  periods: TimePeriod[]
  labels: { a: string; b: string }
  /** Non-zero when the compared plan is another fiscal year, aligned month for month. */
  year_shift?: number
  on_back: () => void
  className?: string
}

/** A compare value in the chosen measure: dollars to the cent, cases to the hundredth. */
export function format_compare_value(value: number, measure: CompareMeasure): string {
  return measure === "volume_dollars" ? exactCurrencyFormatter(value) : format_cases(value)
}

/** Δ% to one decimal with its sign, or an em dash when the compared side is 0. */
export function format_delta_pct(pair: CompareCellPair): string {
  const pct = compare_delta_pct(pair)
  if (pct === null) return "—"
  const rounded = Math.round(pct * 10) / 10
  return `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)}%`
}

function delta_class(delta: number): string {
  if (Math.abs(delta) < 0.005) return "text-gray-500"
  return delta > 0 ? "text-emerald-700" : "text-red-700"
}

function PairCells({ pair, measure }: { pair: CompareCellPair; measure: CompareMeasure }) {
  const delta = Math.round((pair.a - pair.b) * 100) / 100
  return (
    <>
      <td class="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">{format_compare_value(pair.a, measure)}</td>
      <td class="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-gray-600">{format_compare_value(pair.b, measure)}</td>
      <td class={`whitespace-nowrap px-2 py-1.5 text-right tabular-nums ${delta_class(delta)}`}>
        {delta > 0 ? "+" : ""}
        {format_compare_value(delta, measure)}
      </td>
      <td class={`whitespace-nowrap border-r border-gray-200 px-2 py-1.5 text-right tabular-nums ${delta_class(delta)}`}>
        {format_delta_pct(pair)}
      </td>
    </>
  )
}

function CompareRow({
  row,
  measure,
  period_keys,
  depth,
  expanded,
  on_toggle,
}: {
  row: VersionCompareRow
  measure: CompareMeasure
  period_keys: string[]
  depth: number
  expanded?: boolean
  on_toggle?: () => void
}) {
  const has_children = (row.children?.length ?? 0) > 0
  return (
    <tr class={`border-b border-gray-100 ${depth === 0 ? "bg-white font-medium" : "bg-gray-50/60 text-gray-700"}`}>
      <td class={`sticky left-0 z-10 whitespace-nowrap border-r border-gray-200 px-3 py-1.5 ${depth === 0 ? "bg-white" : "bg-gray-50"}`}>
        <div class="flex items-center gap-1" style={{ paddingLeft: `${depth * 20}px` }}>
          {has_children ? (
            <Button type="button" variant="ghost" className="h-6 w-6 p-1" onClick={on_toggle} aria-label={expanded ? "Collapse" : "Expand"}>
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </Button>
          ) : (
            <span class="inline-block h-6 w-6" />
          )}
          <span class="text-sm">{row.name}</span>
        </div>
      </td>
      {period_keys.map((period: string) => (
        <PairCells key={period} pair={row.periods[period] ?? { a: 0, b: 0 }} measure={measure} />
      ))}
      <PairCells pair={row.total} measure={measure} />
    </tr>
  )
}

/**
 * Version comparison as its own table — shown in place of the grid while a comparison is active.
 * Rows are customers, expandable to their product lines; each period has four columns (this
 * version, the compared one, Δ, Δ%), the year totals sit at the right and a totals row at the
 * bottom. Everything on it comes from `build_version_compare_table`; nothing here is editable.
 */
export function VersionCompareView({
  this_version,
  compared_version,
  periods,
  labels,
  year_shift = 0,
  on_back,
  className = "",
}: VersionCompareViewProps) {
  const [measure, set_measure] = useState<CompareMeasure>("volume_dollars")
  const [expanded, set_expanded] = useState<Set<string>>(new Set())
  const period_keys = useMemo(() => periods.map((p: TimePeriod) => p.period_key), [periods])
  const table = useMemo(
    () => build_version_compare_table(this_version, compared_version, period_keys, measure),
    [this_version, compared_version, period_keys, measure]
  )

  function toggle(key: string): void {
    set_expanded((prev: Set<string>) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const header_groups = [...periods.map((p: TimePeriod) => format_period_header(p)), "Annual total"]

  return (
    <div class={`space-y-3 ${className}`} data-testid="version-compare-view">
      <div class="flex flex-wrap items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50/70 px-4 py-2.5">
        <Button type="button" variant="outline" size="sm" onClick={on_back}>
          <ArrowLeft className="mr-1 h-3.5 w-3.5" />
          Back to grid
        </Button>
        <span class="text-sm text-indigo-900">
          <strong>{labels.a}</strong> vs <strong>{labels.b}</strong> — Δ is this version minus the compared one.
          {year_shift !== 0
            ? ` Different plan years: months are aligned onto these columns (${year_shift > 0 ? "-" : "+"}${Math.abs(year_shift)} year).`
            : ""}
        </span>
        <div class="ml-auto flex rounded-md border border-gray-300 bg-white p-0.5">
          {(["volume_dollars", "cases_sold"] as CompareMeasure[]).map((m: CompareMeasure) => (
            <Button
              key={m}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => set_measure(m)}
              className={`h-7 px-2.5 text-xs ${
                measure === m
                  ? "border border-blue-300 bg-blue-50 text-blue-800 shadow-sm"
                  : "border border-transparent text-gray-600 hover:bg-gray-100"
              }`}
            >
              {m === "volume_dollars" ? "Revenue" : "Cases"}
            </Button>
          ))}
        </div>
      </div>

      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="min-w-full border-collapse text-sm">
          <thead>
            <tr class="border-b border-gray-200 bg-gray-50 text-xs text-gray-700">
              <th class="sticky left-0 z-20 border-r border-gray-200 bg-gray-50 px-3 py-2 text-left font-medium">Customer</th>
              {header_groups.map((label: string, index: number) => (
                <th
                  key={`${label}-${index}`}
                  colSpan={4}
                  class={`border-r border-gray-200 px-2 py-2 text-center font-medium ${index === header_groups.length - 1 ? "bg-gray-100" : ""}`}
                >
                  {label}
                </th>
              ))}
            </tr>
            <tr class="border-b border-gray-200 bg-gray-50 text-[11px] text-gray-500">
              <th class="sticky left-0 z-20 border-r border-gray-200 bg-gray-50 px-3 py-1 text-left font-normal">
                {table.rows.length} customer{table.rows.length === 1 ? "" : "s"}
              </th>
              {header_groups.map((label: string, index: number) => (
                <>
                  <th key={`${label}-${index}-a`} class="px-2 py-1 text-right font-normal" title={labels.a}>
                    This
                  </th>
                  <th key={`${label}-${index}-b`} class="max-w-[9rem] truncate px-2 py-1 text-right font-normal" title={labels.b}>
                    {labels.b}
                  </th>
                  <th key={`${label}-${index}-d`} class="px-2 py-1 text-right font-normal">
                    Δ
                  </th>
                  <th key={`${label}-${index}-p`} class="border-r border-gray-200 px-2 py-1 text-right font-normal">
                    Δ%
                  </th>
                </>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.length === 0 ? (
              <tr>
                <td colSpan={1 + header_groups.length * 4} class="px-3 py-6 text-center text-sm text-gray-500">
                  Neither version has rows in these periods.
                </td>
              </tr>
            ) : null}
            {table.rows.map((row: VersionCompareRow) => (
              <>
                <CompareRow
                  key={row.key}
                  row={row}
                  measure={measure}
                  period_keys={period_keys}
                  depth={0}
                  expanded={expanded.has(row.key)}
                  on_toggle={() => toggle(row.key)}
                />
                {expanded.has(row.key)
                  ? (row.children ?? []).map((line: VersionCompareRow) => (
                      <CompareRow key={line.key} row={line} measure={measure} period_keys={period_keys} depth={1} />
                    ))
                  : null}
              </>
            ))}
          </tbody>
          <tfoot>
            <tr class="border-t-2 border-gray-300 bg-gray-50 font-semibold">
              <td class="sticky left-0 z-10 border-r border-gray-200 bg-gray-50 px-3 py-2">Total</td>
              {period_keys.map((period: string) => (
                <PairCells key={period} pair={table.total.periods[period] ?? { a: 0, b: 0 }} measure={measure} />
              ))}
              <PairCells pair={table.total.total} measure={measure} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("format_delta_pct / format_compare_value", () => {
    it("signs the percentage and dashes a zero compared side", () => {
      expect(format_delta_pct({ a: 110, b: 100 })).toBe("+10.0%")
      expect(format_delta_pct({ a: 90, b: 100 })).toBe("-10.0%")
      expect(format_delta_pct({ a: 5, b: 0 })).toBe("—")
    })

    it("formats the chosen measure", () => {
      expect(format_compare_value(1234.5, "volume_dollars")).toBe("$1,234.50")
      expect(format_compare_value(8.4, "cases_sold")).toBe("8.4")
    })
  })
}
